import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { OrganisationService } from 'src/app/services/api/organisation/organisation.service';

// eslint-disable-next-line @angular-eslint/component-class-suffix
class ValueUsedMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && (control.dirty || control.touched));
  }
}

type OrganisationAccountCreationData = {
  organisations_ids: number[];
};

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-dialog-organisation-account-creation',
  templateUrl: './dialog-organisation-account-creation.component.html',
  styleUrls: ['./dialog-organisation-account-creation.component.scss'],
})
export class DialogOrganisationAccountCreationComponent implements OnInit, OnDestroy {
  readonly emailRegex =
    // eslint-disable-next-line max-len
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Account form submission
  public accountForm: UntypedFormGroup;

  public createNewAccountResponse = new BehaviorSubject<ResponseState>('DEFAULT');

  public isNameAlreadyInUseSubject = new BehaviorSubject<boolean>(false);
  public isEmailAlreadyInUseSubject = new BehaviorSubject<boolean>(false);
  public invalidEmailSubject = new BehaviorSubject<boolean>(false);

  public matcher = new ValueUsedMatcher();

  public organisations_ids: number[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrganisationAccountCreationData,
    private dialogRef: MatDialogRef<DialogOrganisationAccountCreationComponent>,
    private organisationService: OrganisationService,
  ) {
    this.accountForm = new UntypedFormGroup({
      email: new UntypedFormControl('', [Validators.required, Validators.pattern(this.emailRegex)]),
      name: new UntypedFormControl('', [Validators.required]),
      firstname: new UntypedFormControl(''),
      lastname: new UntypedFormControl(''),
      admin: new UntypedFormControl(false),
      ecoach: new UntypedFormControl(false),
      editor: new UntypedFormControl(false),
    });
    this.accountForm.markAllAsTouched();
  }

  public get f() {
    return this.accountForm;
  }

  ngOnInit(): void {
    this.organisations_ids = this.data.organisations_ids;
  }

  resetEmailError(): void {
    this.isEmailAlreadyInUseSubject.next(false);
    this.invalidEmailSubject.next(false);
  }

  resetNameError(): void {
    this.isNameAlreadyInUseSubject.next(false);
  }

  private resetError(): void {
    this.resetEmailError();
    this.resetNameError();
  }

  invalidForm() {
    return this.accountForm.invalid;
  }

  public registerNewAccount(): void {
    if (this.invalidForm()) {
      return;
    }
    this.resetError();

    const email = this.f.get('email').value;
    const name = this.getTrimmedString(this.f.get('name').value);
    const firstname = this.getTrimmedString(this.f.get('firstname').value);
    const lastname = this.getTrimmedString(this.f.get('lastname').value);

    if (this.createNewAccountResponse.value === 'DEFAULT') {
      this.createNewAccountResponse.next('LOADING');
      this.subscriptions.push(
        this.organisationService
          .createAccountAsOrganisationManager(email, this.organisations_ids, name, firstname, lastname)
          .subscribe({
            next: () => {
              this.createNewAccountResponse.next('SUCCESS');
              setTimeout(() => {
                this.createNewAccountResponse.next('DEFAULT');
                this.dialogRef.close();
              }, 2500);
            },
            error: (value) => {
              const errors = value.error.errors[0]?.meta?.errors;
              if (errors) {
                const emailKey = 'data.attributes.email';
                if (emailKey in errors) {
                  const errorCode = errors[emailKey].at(0)?.error_code?.toString();
                  if (errorCode === "3007") {
                    this.isEmailAlreadyInUseSubject.next(true);
                  } else {
                    this.invalidEmailSubject.next(true);
                  }
                }
                const nameKey = 'data.attributes.name';
                if (nameKey in errors) {
                  this.isNameAlreadyInUseSubject.next(true);
                }
              }
              this.createNewAccountResponse.next('FAILURE');
              setTimeout(() => {
                this.createNewAccountResponse.next('DEFAULT');
              }, 2500);
            },
          }),
      );
    }
  }

  private getTrimmedString(s: string | null): string | undefined {
    return s || s === '' ? s.trim() : undefined;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
